import { useState } from "react";
import { evalCond, isRequired } from "../lib/cond";
import { useLang } from "../lib/context";
import { Plus, Trash2, Eye, EyeOff } from "lucide-react";

const tid = (k) => `field-${k.replace(/[^a-zA-Z0-9]+/g, "-")}`;
const base = "fx-input w-full min-h-[48px] rounded-xl border bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm transition-all duration-200 placeholder:text-slate-400 hover:border-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/15 disabled:bg-slate-50";

function optionsFor(f, ctx) {
  const { provider, entityRules, roleOptions, outlets, accounts, entityTypes, kind } = ctx;
  switch (f.type) {
    case "variant_select": return ((provider || {})[`${kind}_variants`] || []).map((v) => ({ value: v.code, label: `${v.label}${v.min_funding ? ` (min. deposit Rs ${v.min_funding})` : ""}` }));
    case "entity_select": return (entityTypes || []).filter((e) => e.code !== "individual").map((e) => ({ value: e.code, label: e.label }));
    case "role_multiselect": return ((entityRules || {}).roles || []).map((r) => ({ value: r, label: roleOptions[r] }));
    case "outlet_select": return (outlets || []).map((o) => ({ value: o.id, label: `${o.name} - ${o.city} ${o.pin}` }));
    case "account_select": return (accounts || []).map((a) => ({ value: a.id, label: `${a.bank} ${a.number_mask} · ${a.type} · ${a.settlement_status.replace(/_/g, " ")}`, disabled: a.settlement_status !== "approved_for_settlement" }));
    case "qr_type_select": return ((provider || {}).qr_types || []).map((q) => ({ value: q, label: q === "static" ? "Static QR" : "Dynamic QR" }));
    case "pos_plan_select": return ((provider || {}).pos_plans || []).map((p) => ({ value: p.code, label: p.label }));
    default: return f.options || [];
  }
}

function Control({ f, value, onChange, ctx, id, invalid }) {
  const [show, setShow] = useState(false);
  const cls = `${base} ${invalid ? "border-red-500" : "border-slate-300"}`;
  const opts = optionsFor(f, ctx);
  const aria = { id, "aria-invalid": invalid || undefined, "aria-describedby": `${id}-help`, "data-testid": id };
  if (["select", "variant_select", "entity_select", "outlet_select", "account_select", "qr_type_select", "pos_plan_select"].includes(f.type)) {
    return (
      <select {...aria} className={cls} value={value || ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select…</option>
        {opts.map((o) => <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}
      </select>
    );
  }
  if (f.type === "radio") {
    return (
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-labelledby={`${id}-label`} data-testid={id}>
        {opts.map((o) => (
          <label key={o.value} className={`flex min-h-[46px] cursor-pointer items-center gap-2 rounded-xl border px-5 py-2 text-sm font-medium transition-all duration-200 active:scale-[0.97] ${value === o.value ? "border-transparent sx-grad text-white shadow-md shadow-indigo-500/25" : "border-slate-300 bg-white hover:border-indigo-300 hover:bg-indigo-50/50"}`}>
            <input type="radio" className="sr-only" name={id} checked={value === o.value} onChange={() => onChange(o.value)} data-testid={`${id}-${o.value}`} />
            {o.label}
          </label>
        ))}
      </div>
    );
  }
  if (["multiselect", "role_multiselect"].includes(f.type)) {
    const arr = value || [];
    return (
      <div className="flex flex-wrap gap-2" data-testid={id}>
        {opts.map((o) => (
          <label key={o.value} className={`flex min-h-[46px] cursor-pointer items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition-all duration-200 ${arr.includes(o.value) ? "border-indigo-400 bg-indigo-50 text-indigo-800 shadow-sm" : "border-slate-300 bg-white hover:border-indigo-300"}`}>
            <input type="checkbox" checked={arr.includes(o.value)} data-testid={`${id}-${o.value}`}
              onChange={(e) => onChange(e.target.checked ? [...arr, o.value] : arr.filter((x) => x !== o.value))} className="h-4 w-4 accent-indigo-600" />
            {o.label}
          </label>
        ))}
        {!opts.length && <span className="text-sm text-slate-500">No options available yet.</span>}
      </div>
    );
  }
  if (f.type === "checkbox") {
    return (
      <label className="flex min-h-[48px] cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 text-sm font-medium transition-colors hover:border-indigo-300">
        <input type="checkbox" {...aria} checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-5 w-5 accent-indigo-600" />
        <span>{f.label}</span>
      </label>
    );
  }
  if (f.type === "textarea") return <textarea {...aria} rows={3} className={cls} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  if (f.type === "readonly") return <div className="rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-500" data-testid={id}>{value || "Pending provider confirmation"}</div>;
  if (f.type === "secret") {
    return (
      <div className="relative">
        <input {...aria} type={show ? "text" : "password"} autoComplete="off" inputMode="numeric" className={cls} value={value || ""} onChange={(e) => onChange(e.target.value.replace(/\s/g, ""))} onPaste={f.key.endsWith("confirm") ? (e) => e.preventDefault() : undefined} />
        <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-2.5 text-slate-500" aria-label="Toggle visibility">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
      </div>
    );
  }
  const type = { date: "date", number: "number", email: "email", tel: "tel", url: "url" }[f.type] || "text";
  const upper = /pan|ifsc|gstin|cin|llpin/i.test(f.key.split(".").pop());
  return <input {...aria} type={type} placeholder={f.placeholder} className={cls} value={value ?? ""} onChange={(e) => onChange(upper ? e.target.value.toUpperCase() : e.target.value)} />;
}

export function Field({ f, value, onChange, data, meta, item, error, ctx, keyPath }) {
  const { lang, t } = useLang();
  if (!evalCond(f.show, data, meta, item)) return null;
  const id = tid(keyPath || f.key);
  const lbl = lang === "hi" && f.label_hi ? f.label_hi : f.label;
  if (f.type === "heading") return <h3 className="col-span-full mt-3 flex items-center gap-2 text-base font-bold text-slate-900"><span className="h-5 w-1.5 rounded-full bg-gradient-to-b from-indigo-500 to-cyan-400" />{lbl}</h3>;
  if (f.type === "repeat") return <Repeat f={f} value={value} onChange={onChange} data={data} meta={meta} ctx={ctx} errors={ctx.errors} render={renderSub} />;
  const req = isRequired(f, data, meta, item);
  const wide = ["textarea", "multiselect", "role_multiselect", "radio", "checkbox"].includes(f.type);
  return (
    <div className={wide ? "col-span-full" : ""}>
      {f.type !== "checkbox" && (
        <label htmlFor={id} id={`${id}-label`} className="mb-1.5 flex items-center gap-2 text-sm font-medium text-slate-800">
          {lbl}
          {lang === "hi" && f.label_hi && <span className="text-xs text-slate-400">({f.label})</span>}
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${req ? "bg-rose-50 text-rose-600" : "bg-slate-100 text-slate-500"}`}>{req ? (f.req_if ? t("Required (conditional)") : t("Required")) : t("Optional")}</span>
        </label>
      )}
      <Control f={f} value={value} onChange={onChange} ctx={ctx} id={id} invalid={!!error} />
      {f.help && <p id={`${id}-help`} className="mt-1 text-xs text-slate-500">{f.help}</p>}
      {error && <p role="alert" data-testid={`${id}-error`} className="mt-1 text-xs font-medium text-red-600">{t(error)}</p>}
    </div>
  );
}

const SubField = ({ render, ...p }) => render(p);

function renderSub(props) {
  return Field(props);
}

function Repeat({ f, value, onChange, data, meta, ctx, errors, render }) {
  const items = value || [];
  const set = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  return (
    <div className="col-span-full space-y-3" data-testid={tid(f.key)}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-900">{f.label} {f.req && <span className="text-[10px] uppercase text-red-600">Required</span>}</div>
          {f.help && <p className="text-xs text-slate-500">{f.help}</p>}
        </div>
        <button type="button" data-testid={`${tid(f.key)}-add`} onClick={() => onChange([...items, {}])} className="flex min-h-[44px] items-center gap-1 rounded-xl sx-grad px-4 text-sm font-semibold text-white shadow-md shadow-indigo-500/25 transition-transform hover:-translate-y-0.5 active:scale-95">
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>
      {errors?.[f.key] && <p role="alert" className="text-xs font-medium text-red-600" data-testid={`${tid(f.key)}-error`}>{errors[f.key]}</p>}
      {items.map((it, i) => (
        <div key={i} className="fade-up rounded-2xl border border-indigo-100 bg-gradient-to-br from-white to-indigo-50/40 p-5 shadow-sm" data-testid={`${tid(f.key)}-item-${i}`}>
          <div className="mb-3 flex items-center justify-between">
            <span className="font-mono text-xs font-semibold text-slate-500">#{i + 1} {it.name || ""}</span>
            <button type="button" aria-label="Remove" data-testid={`${tid(f.key)}-remove-${i}`} onClick={() => onChange(items.filter((_, j) => j !== i))} className="rounded p-2 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {f.fields.map((sf) => (
              <SubField key={sf.key} render={render} f={sf} value={it[sf.key]} item={it} data={data} meta={meta} ctx={ctx} keyPath={`${f.key}-${i}-${sf.key}`}
                error={errors?.[`${f.key}[${i}].${sf.key}`]} onChange={(v) => set(i, sf.key, v)} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
